import { Pricing } from '@joinomu/ui'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase, StripeService } from '@joinomu/shared'
import { useEffect, useState } from 'react'

export function PricingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [patientId, setPatientId] = useState<string | null>(null)
  const [loadingPatientId, setLoadingPatientId] = useState(false)
  const [patientIdError, setPatientIdError] = useState<string | null>(null)

  useEffect(() => {
    const getPatientData = async () => {
      if (user) {
        setLoadingPatientId(true)
        setPatientIdError(null)
        
        try {
          console.log('🔍 Fetching patient ID for user:', user.id)
          const { data: patient, error } = await supabase
            .from('patients')
            .select('id')
            .eq('profile_id', user.id)
            .single()

          if (error) {
            console.error('❌ Error fetching patient:', error)
            setPatientIdError('Unable to load patient data. Please try again.')
          } else if (patient) {
            console.log('✅ Patient ID loaded:', patient.id)
            setPatientId(patient.id)
          } else {
            console.error('❌ No patient found for user:', user.id)
            setPatientIdError('Patient profile not found. Please contact support.')
          }
        } catch (error) {
          console.error('❌ Error fetching patient profile:', error)
          setPatientIdError('Failed to load patient data. Please try again.')
        } finally {
          setLoadingPatientId(false)
        }
      }
    }

    getPatientData()
  }, [user])

  const handleClose = () => {
    // Navigate back to patient dashboard if user is logged in
    if (user) {
      navigate('/patient/dashboard')
    } else {
      // Navigate to landing page if not logged in
      navigate('/')
    }
  }

  const handleCheckout = async (priceId: string) => {
    if (!user) {
      alert('Please sign in to continue with checkout.')
      return
    }

    if (loadingPatientId) {
      alert('Please wait for patient data to load before proceeding.')
      return
    }

    if (patientIdError) {
      alert(`Cannot proceed with checkout: ${patientIdError}`)
      return
    }

    if (!patientId) {
      console.error('❌ Patient ID not available for checkout')
      alert('Patient data is required for checkout. Please refresh the page and try again.')
      return
    }

    try {
      console.log('🚀 Starting Stripe checkout with environment-aware StripeService...')
      console.log('Parameters:', { priceId, patientId, userId: user.id })

      // Use StripeService with environment-aware database detection
      const result = await StripeService.createCheckoutSession({
        priceId,
        patientId,
        successUrl: `${window.location.origin}/patient/dashboard?success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/patient/pricing?canceled=true`
      })

      if (result.success && result.url) {
        console.log('✅ Checkout session created successfully:', result.sessionId)
        // Redirect to Stripe Checkout
        window.location.href = result.url
      } else {
        throw new Error(result.error || 'Failed to create checkout session')
      }
    } catch (error) {
      console.error('❌ Checkout error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Please try again.'
      alert(`Failed to start checkout: ${errorMessage}`)
    }
  }

  return (
    <Pricing 
      open={true}
      onOpenChange={(open: boolean) => {
        if (!open) {
          handleClose()
        }
      }}
      userId={user?.id}
      patientId={patientId || undefined}
      onCheckout={handleCheckout}
    />
  )
}